/**
 * `v-tooltip` — a small replacement for the v-tooltip package.
 *
 * Why not a library: v-tooltip itself is Vue 2 only, and floating-vue (its
 * Vue 3 successor) throws on teardown under Vue 3 —
 * `$_detachPopperNode` reads `parentNode` of an element Vue has already
 * removed, so every page with tooltips logged errors when it unmounted, and
 * it cannot be applied to a component at all.
 *
 * This directive does what the app actually used: a text tooltip on hover or
 * focus, placed top / bottom / left / right through the modifier. Positioning
 * uses @popperjs/core, which is already a dependency.
 *
 * The markup is deliberately the same as v-tooltip's — `.tooltip`,
 * `.tooltip-inner`, `.tooltip-arrow` and an `x-placement` attribute — so
 * `_tooltip.scss` styles it exactly as before and the tooltips look
 * unchanged.
 */

import { createPopper } from '@popperjs/core';

const PLACEMENTS = ['top', 'bottom', 'left', 'right'];

/** Builds the tooltip element, matching v-tooltip's DOM. */
function createTooltipNode(text) {
	const node = document.createElement('div');
	node.className = 'tooltip';
	node.setAttribute('role', 'tooltip');
	node.setAttribute('aria-hidden', 'true');

	const inner = document.createElement('div');
	inner.className = 'tooltip-inner';
	inner.textContent = text;

	const arrow = document.createElement('div');
	arrow.className = 'tooltip-arrow';
	arrow.setAttribute('data-popper-arrow', '');

	node.appendChild(inner);
	node.appendChild(arrow);
	return node;
}

function placementOf(binding) {
	const modifier = Object.keys(binding.modifiers || {}).find((m) => PLACEMENTS.includes(m));
	return modifier || 'top';
}

function textOf(binding) {
	const value = binding.value;
	if (value === null || value === undefined) return '';
	return typeof value === 'object' ? value.content || '' : String(value);
}

function destroy(el) {
	const state = el.__tooltip;
	if (!state) return;

	el.removeEventListener('mouseenter', state.show);
	el.removeEventListener('mouseleave', state.hide);
	el.removeEventListener('focus', state.show);
	el.removeEventListener('blur', state.hide);

	if (state.popper) state.popper.destroy();
	// The node lives on <body>, so it is still there even when Vue has
	// already detached `el` — which is exactly the case floating-vue got
	// wrong. Guard anyway.
	if (state.node && state.node.parentNode) state.node.parentNode.removeChild(state.node);

	delete el.__tooltip;
}

function setup(el, binding) {
	destroy(el);

	const text = textOf(binding);
	if (!text) return; // nothing to show, so nothing to wire up

	const placement = placementOf(binding);
	const state = { text, placement, node: null, popper: null };

	state.show = function () {
		if (state.node) return;
		state.node = createTooltipNode(state.text);
		document.body.appendChild(state.node);
		state.popper = createPopper(el, state.node, {
			placement: state.placement,
			modifiers: [{ name: 'offset', options: { offset: [0, 8] } }]
		});
		// Mirror v-tooltip's attributes, which the stylesheet keys on.
		state.node.setAttribute('x-placement', state.placement);
		state.node.setAttribute('aria-hidden', 'false');
	};

	state.hide = function () {
		if (state.popper) {
			state.popper.destroy();
			state.popper = null;
		}
		if (state.node && state.node.parentNode) state.node.parentNode.removeChild(state.node);
		state.node = null;
	};

	el.addEventListener('mouseenter', state.show);
	el.addEventListener('mouseleave', state.hide);
	el.addEventListener('focus', state.show);
	el.addEventListener('blur', state.hide);

	el.__tooltip = state;
}

export default {
	mounted: setup,
	updated(el, binding) {
		const state = el.__tooltip;
		// Re-wire only when something actually changed, so hovering is not
		// interrupted by unrelated re-renders.
		if (!state || state.text !== textOf(binding) || state.placement !== placementOf(binding)) {
			setup(el, binding);
		}
	},
	beforeUnmount: destroy,
	unmounted: destroy
};
